import type { IMailAccount, IRichPart, MailVersionStatus } from './mail-template-contracts';

/**
 * survey-api's email designs (SR-523 stage 6), under `mail.templates`: the frame every mail is
 * drawn in. `veritant` — the band, the card, the colours, the font, the width and a footer — is
 * edited and versioned as a template is (a draft, published now or from a date, withdrawn before
 * it takes effect); `plain` is a bare body, a personal note's look, and cannot be edited. A
 * template's version names its design (`settings.design`); the worker resolves the design's
 * current version at send time.
 */

export type MailDesignCode = 'veritant' | 'plain' | (string & {});

/** `#rrggbb`. */
export type HexColor = string;

export interface IMailDesignBand {
  show: boolean;
  text: string;
  /** https, on Mail:Images:AllowedHosts; "" for none. */
  logoUrl: string;
  background: HexColor;
  color: HexColor;
}

export interface IMailDesignColors {
  text: HexColor;
  muted: HexColor;
  /** Links and buttons. */
  accent: HexColor;
  heading: HexColor;
  pageBackground: HexColor;
  card: HexColor;
  border: HexColor;
  summaryBackground: HexColor;
}

export interface IMailDesignSettings {
  band: IMailDesignBand;
  colors: IMailDesignColors;
  /** One of the safe stacks (`DESIGN_FONTS`). */
  font: string;
  /** The card's width in pixels, 480–720. */
  width: number;
  /** Small print at the bottom of every mail in the design; placeholders `support_contact` and
   *  `postal_address`. */
  footer: IRichPart;
}

export interface IMailDesignVersion {
  version: number;
  settings: IMailDesignSettings;
  note: string;
  createdAt: Nullable<string>;
  createdBy: Nullable<IMailAccount>;
  effectiveAt: Nullable<string>;
  publishedAt: Nullable<string>;
  withdrawnAt: Nullable<string>;
  status: MailVersionStatus;
}

export interface IMailDesignDraft {
  settings: IMailDesignSettings;
  note: string;
  revision: number;
  updatedAt: Nullable<string>;
  updatedBy: Nullable<IMailAccount>;
}

/** `GET /admin/mail-designs`: each design and the state of its versions. */
export interface IMailDesignSummary {
  code: MailDesignCode;
  name: string;
  editable: boolean;
  /** The version mails go with now, its settings when the server gives them. */
  current: Nullable<{ version: number; effectiveAt?: Nullable<string>; settings?: IMailDesignSettings }>;
  scheduled: Nullable<{ version: number; effectiveAt?: Nullable<string> }>;
  draft: boolean;
}

/** `GET /admin/mail-designs/{code}`. */
export interface IMailDesign {
  code: MailDesignCode;
  name?: string;
  /** The current version's. */
  settings: IMailDesignSettings;
  /** The current version's number — 0 while the default is. */
  current: number;
  /** The font stacks survey-api accepts. */
  fonts: string[];
  /** Newest first; the last is version 0, the default. */
  versions: IMailDesignVersion[];
  draft: Nullable<IMailDesignDraft>;
}

/** `POST /admin/mail-designs/{code}/preview`: a template drawn with the given settings. */
export interface IMailDesignPreviewRequest {
  settings: IMailDesignSettings;
  template?: string;
  survey?: Nullable<string>;
  role?: string;
  attached?: boolean;
}

/** The safe font stacks a design may name, by the first family's name. */
export const DESIGN_FONTS: readonly { label: string; value: string }[] = [
  { label: 'Arial', value: 'Arial, Helvetica, sans-serif' },
  { label: 'Helvetica', value: 'Helvetica, Arial, sans-serif' },
  { label: 'Verdana', value: 'Verdana, Geneva, sans-serif' },
  { label: 'Tahoma', value: 'Tahoma, Verdana, sans-serif' },
  { label: 'Trebuchet MS', value: 'Trebuchet MS, Helvetica, sans-serif' },
  { label: 'Georgia', value: 'Georgia, Times New Roman, serif' },
  { label: 'Times New Roman', value: 'Times New Roman, Times, serif' }
];

export const DESIGN_WIDTH = { min: 480, max: 720 } as const;

/** The `veritant` design's defaults: the submission confirmation's look. */
export const DEFAULT_DESIGN_SETTINGS: IMailDesignSettings = {
  band: { show: true, text: 'FORBES | VERITANT', logoUrl: '', background: '#022e59', color: '#ffffff' },
  colors: {
    text: '#022138',
    muted: '#4e6478',
    accent: '#0556cc',
    heading: '#022e59',
    pageBackground: '#fafafa',
    card: '#ffffff',
    border: '#d9e1ea',
    summaryBackground: '#eef8fd'
  },
  font: 'Arial, Helvetica, sans-serif',
  width: 600,
  footer: { doc: { type: 'doc', content: [{ type: 'paragraph' }] }, html: '' }
};
